import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { MarketingShell } from "@/components/marketing-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Mail, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { redirectIfAuthenticated } from "@/lib/marketing-guards";

export const Route = createFileRoute("/contact")({
  beforeLoad: redirectIfAuthenticated,
  head: () => ({
    meta: [
      { title: "Contact, Super Affiliate OS" },
      {
        name: "description",
        content:
          "Get in touch with the Super Affiliate OS team. Sales, support, and partnership inquiries welcome.",
      },
      { property: "og:title", content: "Contact, Super Affiliate OS" },
      {
        property: "og:description",
        content: "Get in touch with the Super Affiliate OS team.",
      },
    ],
  }),
  component: ContactPage,
});

function ContactPage() {
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    // Placeholder, wired to a real endpoint in a future release.
    await new Promise((r) => setTimeout(r, 600));
    setSubmitting(false);
    toast.success("Thanks, we'll be in touch within one business day.");
    (e.target as HTMLFormElement).reset();
  };

  return (
    <MarketingShell>
      <section className="mx-auto max-w-3xl px-6 py-20 text-center">
        <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">Talk to us</h1>
        <p className="mx-auto mt-4 max-w-xl text-lg text-muted-foreground">
          Sales questions, custom plans, partnership ideas, or feedback, we'd love to hear it.
        </p>
      </section>

      <section className="mx-auto grid max-w-5xl gap-8 px-6 pb-24 lg:grid-cols-[1fr_1.4fr]">
        <div className="space-y-6">
          <div className="rounded-xl border border-border bg-card p-6">
            <Mail className="h-5 w-5 text-primary" />
            <h3 className="mt-3 font-semibold">Email</h3>
            <p className="mt-1 text-sm text-muted-foreground">hello@superaffiliateos.com</p>
          </div>
          <div className="rounded-xl border border-border bg-card p-6">
            <MessageSquare className="h-5 w-5 text-primary" />
            <h3 className="mt-3 font-semibold">Response time</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              We reply to every message within one business day.
            </p>
          </div>
        </div>

        <form onSubmit={onSubmit} className="space-y-4 rounded-xl border border-border bg-card p-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input id="name" required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="company">Company</Label>
            <Input id="company" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="message">Message</Label>
            <Textarea id="message" rows={6} required />
          </div>
          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting ? "Sending…" : "Send message"}
          </Button>
        </form>
      </section>
    </MarketingShell>
  );
}
